import React, { useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { navSelector } from 'common/store/modules/competition-nav/selectors';

import DefaultCellRendererContainer from 'custom-table/js/components/DefaultCell/DefaultCellRendererContainer';

const depsLoaded = (resolve) => {
    if (typeof PULSE.app.MatchPopup !== 'undefined') {
        resolve();
    } else {
        document.addEventListener(
            PULSE.app.common.CONSTANTS.MatchPopupWidget.READY,
            resolve
        );
    }
};
const DEPS_LOADED = new Promise(depsLoaded);
const MAX_ITEMS = 5;

/*
    FormCellRenderer
    Custom cell renderer for Form of a team - using the form value ie. 'WWWLD'
*/
const FormCellRenderer = ({ className, value, ...additionalProps }) => {
    const filters = useSelector(navSelector);

    const formValue = useMemo(() => {
        if (value.form) {
            const formLetters = value.form.split('');
            const formObjects = [];
            let matchesInForm = 0;
            formLetters
                .slice(-MAX_ITEMS)
                .reverse()
                .forEach((formLetter) => {
                    const matchObject = { letter: formLetter };
                    if (formLetter !== '-' && formLetter !== 'B') {
                        matchObject.matchIndex = matchesInForm;
                        matchesInForm++;
                    }
                    formObjects.push(matchObject);
                });

            formObjects.reverse();

            while (formObjects.length < MAX_ITEMS) {
                formObjects.push({ letter: '-' });
            }

            return formObjects;
        }

        return [];
    }, [value]);

    if (!value.form || value.form === '-') {
        return (
            <DefaultCellRendererContainer
                className={cx(
                    className,
                    'custom-table__form-item custom-table__form-item--empty'
                )}
                {...additionalProps}
            >
                <span> - </span>
            </DefaultCellRendererContainer>
        );
    }

    const loadingIcon = PULSE.app.templating.render(
        {
            cssClass: ``,
            name: 'loading-dark'
        },
        'common.svg-icon'
    );

    const caretDown = PULSE.app.templating.render(
        {
            cssClass: `caret`,
            name: 'caret-down'
        },
        'common.svg-icon'
    );

    const formCellMarkup = formValue.map((formObj, index) => {
        return (
            <li
                key={`${value.teamId}_${formObj.letter}_${index}`}
                className={`custom-table__form-item custom-table__form-item--${formObj.letter.toLowerCase()} js-ladder-form`}
                data-form-index={value.teamId}
                onMouseOver={
                    formObj.letter !== 'B' && formObj.letter !== '-'
                        ? (event) => {
                              // match popup loaded
                              DEPS_LOADED.then(() => {
                                  if (filters) {
                                      new PULSE.app.MatchPopup(event.target, {
                                          teamId: value.teamId,
                                          formIndex: formObj.matchIndex,
                                          competition: filters.competition?.id,
                                          compSeason: filters.season?.id,
                                          gameweek: filters.round?.roundNumber,
                                          byes: [],
                                          scrollBoundary:
                                              event.target.closest(
                                                  '.js-scrolling-area'
                                              ),
                                          isMyLadder: true,
                                          preventReversingMatches: true
                                      });
                                  }
                              });
                          }
                        : null
                }
            >
                {formObj.letter}
                {formObj.letter !== 'B' && formObj.letter !== '-' && (
                    <div className="match-popup js-match-popup">
                        <div className="js-match-popup-content"></div>
                        <div className="list-loader js-popup-loader">
                            <div className="list-loader__icon">
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: loadingIcon
                                    }}
                                ></span>
                            </div>
                            {additionalProps.getTranslation('label.loading')}
                        </div>
                        <span
                            dangerouslySetInnerHTML={{ __html: caretDown }}
                        ></span>
                    </div>
                )}
            </li>
        );
    });

    return (
        <DefaultCellRendererContainer
            className={cx(className, 'custom-table__cell--form')}
            {...additionalProps}
        >
            <ul className="custom-table__form">{formCellMarkup}</ul>
        </DefaultCellRendererContainer>
    );
};

export default FormCellRenderer;

FormCellRenderer.propTypes = {
    className: PropTypes.string,
    value: PropTypes.object,
    additionalProps: PropTypes.object
};
